/**
 * <sticky-header> and the desktop mega menus. docs/04 §4, docs/05 §7, §9.
 *
 * Replaces Dawn's own StickyHeader, which was defined inline in
 * sections/header.liquid. Dawn's version pins from an IntersectionObserver
 * snapshot of the header's bounds taken once at connect time; ours implements
 * the measured threshold formula in docs/04 §4.2, which is a different rule
 * and a Gate 3 check ("extraSpace computed, not hardcoded").
 */

const HEADER_BREAKPOINT = 1024;

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('.header__wrapper');
    this.row = this.querySelector('.header');
    this.topbar = this.querySelector('.topbar');
    this.mode = this.dataset.sticky || 'on_scroll_up';
    if (!this.wrapper || !this.row) return;

    this.lastScroll = window.pageYOffset;
    this.extraSpace = 0;
    this.headerHeight = 0;

    this.onScroll = this.onScroll.bind(this);
    this.measure = this.measure.bind(this);

    this.measure();

    // Chrome heights change with the band (docs/04 §3.2), and the marquee's
    // own height depends on its text size, so this has to be re-measured
    // rather than computed once.
    this.resizeObserver = new ResizeObserver(this.measure);
    this.resizeObserver.observe(this.row);
    if (this.topbar) this.resizeObserver.observe(this.topbar);
    window.addEventListener('resize', this.measure);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.mode === 'always') {
      this.classList.add('stuck', 'sticky-always');
      this.reserve(true);
    }

    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.measure);
    this.resizeObserver?.disconnect();
  }

  /**
   * docs/04 §4.2, reproduced exactly:
   *
   *   let extraSpace = 20;
   *   document.querySelectorAll(".shopify-section-group-header-group")
   *     .forEach(section => { if (section is not the header) extraSpace += section.offsetHeight; });
   *   if (topBar) extraSpace += topBar.offsetHeight;
   *
   * Resolved on the reference at 1440px as 20 + 34 (marquee) + 0 (the empty
   * third section) + 40 (top bar) = 94px. Our header group has no empty third
   * section — docs/38 §2 drops it — so the marquee and the top bar are the
   * only contributors.
   *
   * The top bar is INSIDE this section, so the loop below skips the whole
   * header section and adds it separately. That is why the reference's
   * formula has the apparently redundant second clause.
   */
  measure() {
    const ownSection = this.closest('.shopify-section');
    let extraSpace = 20;

    /*
     * THE STRIPS ABOVE THE HEADER, SUMMED SEPARATELY — docs/54 §1.-31.
     *
     * The same loop, but the total is also published on its own. The floating
     * bar below 768 is `position: fixed`, so it has no idea the announcement
     * strip exists: it was pinned at `top: 8px` and painted UNDER a 32px bar,
     * losing the top 16px of the logo. docs/52 §1 S6 recorded the reference as
     * "sitting 32px down so the announcement strip stays above it" and the
     * implementation used the inset alone.
     *
     * Arithmetic in CSS cannot do this. The strip's height is
     * padding + line-height x text-size, all three merchant settings, and the
     * bar can be removed from the section group entirely — so the only honest
     * value is a measured one. Zero when there is no strip, which is why the
     * CSS fallback is `0px` and not a guess at 32.
     */
    let aboveHeight = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (section === ownSection) return;
      aboveHeight += section.offsetHeight;
    });
    extraSpace += aboveHeight;

    if (this.topbar && this.topbar.offsetParent !== null) {
      extraSpace += this.topbar.offsetHeight;
    }

    this.extraSpace = extraSpace;
    this.headerHeight = this.row.offsetHeight;

    /*
     * THE RESERVE USES THE NATURAL HEIGHT, NOT THE COMPACTED ONE.
     *
     * The header now shrinks when it pins. `reserve()` replaces the space the
     * header vacated in FLOW, and that space is its full height — reserving
     * the compacted height instead would drop the whole page by the
     * difference at the exact moment it pins, which is the jump the original
     * comment below was written to avoid.
     *
     * Captured only while un-stuck, so a resize mid-scroll cannot overwrite
     * it with a compacted measurement. The `||` seeds it for `sticky_mode:
     * always`, which adds `stuck` in connectedCallback before the first
     * measure runs.
     */
    if (!this.classList.contains('stuck') || this.naturalHeight === undefined) {
      /*
       * FLOW CONTRIBUTION, NOT HEIGHT — the overlay makes those different.
       *
       * With `transparent_on_home` the wrapper carries a negative bottom
       * margin equal to its own height, so the row paints over the hero and
       * occupies ZERO vertical space in flow. Reserving `offsetHeight`
       * anyway pushed the whole page down by 116px the moment the header
       * pinned: measured `#MainContent` at 76 un-stuck and 192 stuck, at
       * every width from 1024 up.
       *
       * Height plus margin is what the element actually costs the flow, and
       * it is band-aware for free — below 1024 the media query drops the
       * negative margin, so this reads the full row height there without
       * needing to know the breakpoint.
       */
      const mb = parseFloat(getComputedStyle(this.wrapper).marginBottom) || 0;
      this.naturalHeight = Math.max(0, this.row.offsetHeight + mb);
    }

    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    document.documentElement.style.setProperty('--header-extra-space', `${extraSpace}px`);
    document.documentElement.style.setProperty('--header-above-height', `${aboveHeight}px`);

    // The drawer is anchored below the header rather than to the viewport
    // (docs/16 §3.1), so its height is "whatever is left below the header
    // row". Measured, which covers both of the doc's cases — the at-rest one
    // and the `100vh - header` it specifies once stuck — with one rule.
    const below = Math.max(0, window.innerHeight - this.wrapper.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--nav-drawer-height', `${Math.round(below)}px`);

    if (this.classList.contains('stuck')) this.reserve(true);
  }

  /**
   * Pinning takes .header__wrapper out of flow, which would drop everything
   * below it by the row height in a single frame. Reserving the space keeps
   * the pin from costing a layout shift — docs/45 §4 counts CLS, and this
   * would be the largest single source of it on every page.
   *
   * The reference does not appear to do this; docs/04 §4.4 describes the
   * header "appearing to condense" purely because the bars above scrolled
   * away, which is the motion we want without the jump.
   */
  reserve(on) {
    const h = this.naturalHeight === undefined ? this.headerHeight : this.naturalHeight;
    const next = on ? `${h}px` : '';
    /* GUARDED ON CHANGE. In `sticky_mode: always` the inline script in
       header.liquid has already taken this reservation before first paint, so
       the value here is usually identical -- and writing an identical inline
       style still invalidates layout. Same rule as publishDockHeight and the
       cart mirrors: never write a value that is already set. */
    if (this.style.paddingBottom === next) return;
    this.style.paddingBottom = next;
  }

  onScroll() {
    if (this.mode === 'none') return;

    const y = window.pageYOffset || document.documentElement.scrollTop;
    const goingDown = y > this.lastScroll;

    if (this.mode === 'always') {
      this.lastScroll = y;
      return;
    }

    if (y <= this.extraSpace) {
      // docs/04 §4.3, state 1 — back above the threshold, everything resets
      // and the full chrome stack returns in normal flow.
      if (this.classList.contains('stuck')) {
        this.classList.remove('stuck', 'scroll-up', 'scroll-down');
        document.body.classList.remove('header-scroll-up', 'header-scroll-down');
        this.reserve(false);
        this.measure();
      }
      this.lastScroll = y;
      return;
    }

    if (!this.classList.contains('stuck')) {
      this.classList.add('stuck');
      this.reserve(true);
    }

    if (y > this.headerHeight + this.extraSpace) {
      if (goingDown) {
        this.classList.add('scroll-down');
        this.classList.remove('scroll-up');
        document.body.classList.add('header-scroll-down');
        document.body.classList.remove('header-scroll-up');
        this.closePanels();
      } else if (y < this.lastScroll) {
        this.classList.add('scroll-up');
        this.classList.remove('scroll-down');
        document.body.classList.add('header-scroll-up');
        document.body.classList.remove('header-scroll-down');
      }
    }

    this.lastScroll = y;
  }

  /* ⚠ DAWN'S CART CONFIRMATION CALLS THIS, AND IT DID NOT EXIST.
     ==========================================================================
     `cart-notification.js` calls `this.header.reveal()` before it opens: in
     Dawn the header can be hidden on scroll-down, and the confirmation hangs
     from it. This rewrite replaced Dawn's StickyHeader and dropped the
     method, so that call threw `this.header.reveal is not a function` on
     every successful add — AFTER the item was in the cart and the badge had
     updated, and BEFORE `open()`. Measured 2026-09-29 on mono-sherry25 at
     1440: add, the count goes to 1, and no confirmation ever appears. The
     badge was the only sign anything had happened.

     There is nothing visual left for it to do. The bar no longer leaves the
     screen at any width (base.css, "THE BAR STAYS, AT EVERY WIDTH", merchant
     2026-08-12) — `scroll-down` still toggles, but its transform is `none`. So
     this only moves the state classes to where scrolling up would put them,
     keeping anything that reads `.scroll-up` consistent with a header that is
     in fact showing. Getting the confirmation ON SCREEN is a separate problem
     and lives in cart-notification.js, `anchorToHeader`. */
  reveal() {
    if (!this.classList.contains('scroll-down')) return;
    this.classList.add('scroll-up');
    this.classList.remove('scroll-down');
    document.body.classList.add('header-scroll-up');
    document.body.classList.remove('header-scroll-down');
  }

  closePanels() {
    this.querySelectorAll('.menu__toggle[aria-expanded="true"]').forEach((toggle) => {
      toggle.dispatchEvent(new CustomEvent('megamenu:close', { bubbles: true }));
    });
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

/* ==========================================================================
   Desktop mega menus — docs/05 §7.

   B1 is resolved as click-to-open on a real <button aria-expanded>, with
   hover as a pointer-only assist. See the note in header-menu-desktop.liquid
   for why hover alone cannot be the contract.
   ========================================================================== */

class MegaMenu {
  constructor(root) {
    this.root = root;
    this.overlay = root.querySelector('.header__overlay');
    this.open = null;
    this.hoverTimer = null;

    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.isDesktop = () => window.innerWidth >= HEADER_BREAKPOINT;

    root.querySelectorAll('.menu__item--parent').forEach((item) => this.bind(item));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.open) {
        const toggle = this.open.querySelector('.menu__toggle');
        this.close();
        toggle?.focus();
      }
    });

    document.addEventListener('click', (event) => {
      if (this.open && !this.open.contains(event.target)) this.close();
    });

    // Tabbing out of an open panel closes it. Without this the panel stays
    // open behind the next nav item, covering the page for a keyboard user
    // with no way to dismiss it except Escape.
    root.addEventListener('focusout', (event) => {
      if (!this.open) return;
      if (!this.open.contains(event.relatedTarget)) this.close();
    });

    this.overlay?.addEventListener('click', () => this.close());
  }

  bind(item) {
    const toggle = item.querySelector('.menu__toggle');
    const panel = item.querySelector('.mega-panel');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      if (this.open === item) this.close();
      else this.show(item);
    });

    toggle.addEventListener('megamenu:close', () => this.close());

    if (this.canHover) {
      item.addEventListener('mouseenter', () => {
        if (!this.isDesktop()) return;
        window.clearTimeout(this.hoverTimer);
        this.show(item);
      });
      item.addEventListener('mouseleave', () => {
        if (!this.isDesktop()) return;
        this.hoverTimer = window.setTimeout(() => this.close(), 150);
      });
    }

    this.bindCarousel(panel);
  }

  show(item) {
    if (!this.isDesktop()) return;
    if (this.open && this.open !== item) this.close();

    const toggle = item.querySelector('.menu__toggle');
    const panel = item.querySelector('.mega-panel');
    panel.hidden = false;
    // Two frames: `hidden` has to be gone before the transition class lands,
    // or the panel jumps to its open state with no transition at all.
    requestAnimationFrame(() => panel.classList.add('mega-panel--open'));
    toggle.setAttribute('aria-expanded', 'true');
    item.classList.add('menu__item--open');
    this.root.classList.add('header-outer--menu-open');
    if (this.overlay) this.overlay.hidden = false;
    this.open = item;

    this.refreshCarousel(panel);
  }

  close() {
    if (!this.open) return;
    const item = this.open;
    const toggle = item.querySelector('.menu__toggle');
    const panel = item.querySelector('.mega-panel');

    panel.classList.remove('mega-panel--open');
    toggle.setAttribute('aria-expanded', 'false');
    item.classList.remove('menu__item--open');
    this.root.classList.remove('header-outer--menu-open');
    if (this.overlay) this.overlay.hidden = true;
    this.open = null;

    const done = () => {
      if (!item.classList.contains('menu__item--open')) panel.hidden = true;
    };
    panel.addEventListener('transitionend', done, { once: true });
    // transitionend never fires under prefers-reduced-motion, where the
    // transition duration is 0. docs/43 §8.1.
    window.setTimeout(done, 400);
  }

  /**
   * Native scroll-snap track, no carousel library — docs/42 §3.1.
   */
  bindCarousel(panel) {
    const track = panel.querySelector('[data-mega-track]');
    const nav = panel.querySelector('[data-mega-nav]');
    if (!track || !nav) return;

    const step = () => track.querySelector('.mega-products__item')?.offsetWidth || 240;
    nav.querySelector('[data-mega-prev]')?.addEventListener('click', () => {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    nav.querySelector('[data-mega-next]')?.addEventListener('click', () => {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });
  }

  refreshCarousel(panel) {
    const track = panel.querySelector('[data-mega-track]');
    const nav = panel.querySelector('[data-mega-nav]');
    if (!track || !nav) return;
    // Only offer arrows when there is somewhere to scroll to.
    nav.hidden = track.scrollWidth <= track.clientWidth + 1;
  }
}

/* --------------------------------------------------------------------------
 * DEFERRED PANEL IMAGES
 *
 * `responsive-image` can ship an image as `data-src`/`data-srcset` instead of
 * the real attributes -- see the note in that snippet. The mega menu's
 * featured image uses it, because `loading="lazy"` does NOT save an image
 * inside a `display: none` panel: with no layout box, Chrome cannot apply the
 * near-viewport heuristic and fetches it regardless. Measured on live by
 * weighing what arrives on hydration: 14KB mobile, 28KB desktop, for a menu
 * that is closed on load. (An earlier note said 86KB -- that is the slideshow
 * fetching the same photograph at 1600w, a separate defect.)
 *
 * ⚠ HYDRATION IS BOUND TO THE HEADER, NOT TO THE MEGA MENU'S OPEN HANDLER.
 * The same markup renders twice -- once in `.mega-panel__inner` for the
 * desktop mega menu and once in `.nav-submenu__cards` for the mobile drawer --
 * and those are opened by two different files. One listener on the header
 * covers both, and cannot fall out of step with either when one of them
 * changes.
 *
 * The trigger is the first pointerover, focusin or touchstart anywhere in the
 * header: all three precede the panel actually opening, so the request starts
 * before the image is needed rather than after. `width`, `height` and the
 * aspect-ratio box are already in the markup, so arriving late costs no
 * layout shift.
 * ----------------------------------------------------------------------- */
const hydrateDeferredImages = (scope) => {
  scope.querySelectorAll('responsive-image[data-deferred]').forEach((holder) => {
    const img = holder.querySelector('img[data-src]');
    if (img) {
      const srcset = img.getAttribute('data-srcset');
      /* srcset BEFORE src: setting src first can start a request for the
         fallback that the srcset candidate then supersedes -- two requests
         for one image, which is the thing this whole change is about. */
      if (srcset) {
        img.setAttribute('srcset', srcset);
        img.removeAttribute('data-srcset');
      }
      /* EAGER ON HYDRATION, and this is not a contradiction. `lazy` was doing
         nothing useful here to begin with -- Chrome fetched the image inside a
         `display: none` panel regardless. Once the src is set dynamically the
         heuristic flips the other way and defers it indefinitely, so the
         image had a src, never loaded, and the menu would have opened empty.
         Hydration only runs on real intent -- the visitor has already reached
         the header -- so the honest instruction at that point is "load it". */
      img.setAttribute('loading', 'eager');
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
    }
    holder.removeAttribute('data-deferred');
  });
};

document.querySelectorAll('.header-outer').forEach((root) => {
  if (!root.querySelector('responsive-image[data-deferred]')) return;
  const once = () => {
    hydrateDeferredImages(root);
    ['pointerover', 'focusin', 'touchstart'].forEach((e) => root.removeEventListener(e, once));
  };
  ['pointerover', 'focusin', 'touchstart'].forEach((e) => {
    root.addEventListener(e, once, { passive: true });
  });
});

document.querySelectorAll('.header-outer').forEach((root) => new MegaMenu(root));
